import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  Mic,
  MonitorUp,
  Pause,
  Play,
  ShieldCheck,
  Square,
  X,
} from 'lucide-react';
import { formatDuration, recorderMimeToExtension, recorderMimeType } from '../utils/meetlyAudio';

export type CaptureMode = 'microphone' | 'microphone-and-system';

export interface StoppedRecording {
  file: File;
  durationSeconds: number;
  captureMode: CaptureMode;
}

interface MeetlyRecorderProps {
  onRecordingReady: (recording: StoppedRecording) => void;
  onRecordingInvalidated?: () => void;
  disabled?: boolean;
}

type RecorderPhase = 'idle' | 'starting' | 'recording' | 'paused' | 'stopping' | 'ready';

type AudioWindow = Window & typeof globalThis & {
  webkitAudioContext?: typeof AudioContext;
};

const microphonePreferenceKey = 'meetly:preferred-microphone-id';

const micPermissionStatus = async (): Promise<'granted' | 'denied' | 'prompt' | 'unknown'> => {
  const permissionsApi = (navigator as Navigator & {
    permissions?: {
      query: (options: PermissionDescriptor) => Promise<PermissionStatus>;
    };
  }).permissions;

  if (!permissionsApi?.query) return 'unknown';

  try {
    const response = await permissionsApi.query({
      name: 'microphone' as PermissionName,
    });
    if (response.state === 'granted' || response.state === 'denied') {
      return response.state;
    }
    return 'prompt';
  } catch {
    return 'unknown';
  }
};

const startHintFor = (phase: RecorderPhase, status: string) => {
  if (phase === 'ready') return status || 'Capture ready';
  if (phase === 'starting') return 'Preparing sources';
  if (phase === 'stopping') return 'Finishing capture';
  if (phase === 'recording') return 'Capturing';
  if (phase === 'paused') return 'Capture paused';
  return 'Ready';
};

export function MeetlyRecorder({ onRecordingReady, onRecordingInvalidated, disabled = false }: MeetlyRecorderProps) {
  const [captureMode, setCaptureMode] = useState<CaptureMode>('microphone');
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState('');
  const [phase, setPhase] = useState<RecorderPhase>('idle');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [permissionHint, setPermissionHint] = useState('Checking microphone access...');

  const recorderRef = useRef<MediaRecorder | null>(null);
  const sourceStreamsRef = useRef<MediaStream[]>([]);
  const outputStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const cancelledRef = useRef(false);
  const mountedRef = useRef(true);
  const captureGenerationRef = useRef(0);
  const chunkDurationMsRef = useRef(0);
  const segmentStartMsRef = useRef<number | null>(null);
  const mimeTypeRef = useRef<string>('audio/webm');

  const recorderSupportsPause = typeof window.MediaRecorder !== 'undefined' && 'pause' in MediaRecorder.prototype;
  const isActive = phase === 'starting' || phase === 'recording' || phase === 'paused' || phase === 'stopping';

  const phaseLabel = useMemo(() => {
    if (phase === 'ready') return 'Capture ready';
    if (phase === 'starting') return 'Preparing sources';
    if (phase === 'recording') return `Recording ${formatDuration(elapsedSeconds)}`;
    if (phase === 'paused') return `Paused ${formatDuration(elapsedSeconds)}`;
    if (phase === 'stopping') return 'Finishing capture';
    return 'Ready';
  }, [elapsedSeconds, phase]);

  const releaseCapture = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const tracks = new Set<MediaStreamTrack>();
    sourceStreamsRef.current.forEach((stream) => stream.getTracks().forEach((track) => tracks.add(track)));
    outputStreamRef.current?.getTracks().forEach((track) => tracks.add(track));
    tracks.forEach((track) => track.stop());

    sourceStreamsRef.current = [];
    outputStreamRef.current = null;

    if (audioContextRef.current) {
      void Promise.resolve(audioContextRef.current.close()).catch(() => undefined);
      audioContextRef.current = null;
    }

    chunkDurationMsRef.current = 0;
    segmentStartMsRef.current = null;
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const refreshElapsed = useCallback(() => {
    if (segmentStartMsRef.current === null) return;
    const totalMs = chunkDurationMsRef.current + Math.max(0, Date.now() - segmentStartMsRef.current);
    setElapsedSeconds(Math.floor(totalMs / 1000));
  }, []);

  const beginTimer = useCallback(() => {
    clearTimer();
    segmentStartMsRef.current = Date.now();
    timerRef.current = window.setInterval(refreshElapsed, 250);
  }, [clearTimer, refreshElapsed]);

  const stopTimerAndCaptureSegment = useCallback(() => {
    if (segmentStartMsRef.current === null) return;
    chunkDurationMsRef.current += Date.now() - segmentStartMsRef.current;
    segmentStartMsRef.current = null;
    clearTimer();
  }, [clearTimer]);

  const finalizeDurationSeconds = useCallback(() => {
    const activeSegmentMs = segmentStartMsRef.current === null ? 0 : Math.max(0, Date.now() - segmentStartMsRef.current);
    return Math.max(1, Math.round((chunkDurationMsRef.current + activeSegmentMs) / 1000));
  }, []);

  const loadMicrophones = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      setMicrophones([]);
      setPermissionHint('Could not enumerate microphones.');
      return;
    }

    try {
      const available = await navigator.mediaDevices.enumerateDevices();
      const inputs = available.filter((device) => device.kind === 'audioinput');
      setMicrophones(inputs);
      const savedId = (() => {
        try {
          return window.localStorage.getItem(microphonePreferenceKey);
        } catch {
          return '';
        }
      })();
      const validSavedId = !!savedId && inputs.some((device) => device.deviceId === savedId);

      if (inputs.length === 0) {
        setPermissionHint('No microphone device found.');
      } else {
        setPermissionHint('Microphone detected.');
        setSelectedMicrophoneId((previous) => {
          if (previous && inputs.some((device) => device.deviceId === previous)) {
            return previous;
          }
          if (validSavedId) {
            return savedId;
          }
          return inputs[0].deviceId;
        });
      }
    } catch {
      setMicrophones([]);
      setPermissionHint('Could not read microphone list.');
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void loadMicrophones();
    void (async () => {
      const permission = await micPermissionStatus();
      if (permission === 'denied') {
        setPermissionHint('Microphone permission was denied.');
      } else if (permission === 'prompt') {
        setPermissionHint('Microphone permission is not confirmed yet.');
      } else if (permission === 'granted') {
        setPermissionHint('Microphone permission was granted.');
      } else {
        setPermissionHint('Microphone ready.');
      }
    })();

    return () => {
      mountedRef.current = false;
      captureGenerationRef.current += 1;
      const recorder = recorderRef.current;
      if (recorder) {
        recorder.ondataavailable = null;
        recorder.onerror = null;
        recorder.onstart = null;
        recorder.onpause = null;
        recorder.onresume = null;
        recorder.onstop = null;
        if (recorder.state !== 'inactive') recorder.stop();
      }
      recorderRef.current = null;
      chunksRef.current = [];
      releaseCapture();
    };
  }, [loadMicrophones, releaseCapture]);

  const startRecording = async () => {
    const generation = captureGenerationRef.current + 1;
    captureGenerationRef.current = generation;
    onRecordingInvalidated?.();

    setError(null);
    setPhase('starting');
    setElapsedSeconds(0);
    chunksRef.current = [];
    cancelledRef.current = false;
    chunkDurationMsRef.current = 0;
    segmentStartMsRef.current = null;
    mimeTypeRef.current = recorderMimeType();

    if (!navigator.mediaDevices?.getUserMedia || typeof window.MediaRecorder === 'undefined') {
      setError('Recording is not supported in this browser. Use an up-to-date desktop browser.');
      setPhase('idle');
      return;
    }

    let microphoneStream: MediaStream | null = null;
    let displayStream: MediaStream | null = null;

    const micConstraint = selectedMicrophoneId
      ? { audio: { deviceId: { exact: selectedMicrophoneId } } }
      : { audio: true };

    try {
      let recordingStream: MediaStream;
      if (captureMode === 'microphone-and-system') {
        if (!navigator.mediaDevices.getDisplayMedia) {
          throw new Error('DISPLAY_CAPTURE_UNAVAILABLE');
        }

        const retainSource = (stream: MediaStream) => {
          if (captureGenerationRef.current !== generation || !mountedRef.current) {
            stream.getTracks().forEach((track) => track.stop());
            return stream;
          }
          if (!sourceStreamsRef.current.includes(stream)) {
            sourceStreamsRef.current.push(stream);
          }
          return stream;
        };

        const [microphoneResult, displayResult] = await Promise.allSettled([
          navigator.mediaDevices.getUserMedia(micConstraint as MediaStreamConstraints).then(retainSource),
          navigator.mediaDevices.getDisplayMedia({ video: true, audio: true }).then(retainSource),
        ]);

        if (microphoneResult.status === 'fulfilled') {
          microphoneStream = microphoneResult.value;
        }

        if (displayResult.status === 'fulfilled') {
          displayStream = displayResult.value;
        }

        if (captureGenerationRef.current !== generation || !mountedRef.current) {
          return;
        }

        if (microphoneResult.status === 'rejected') {
          throw microphoneResult.reason;
        }
        if (displayResult.status === 'rejected') {
          throw displayResult.reason;
        }

        if (!displayStream || displayStream.getAudioTracks().length === 0) {
          throw new Error('DISPLAY_AUDIO_MISSING');
        }

        const AudioContextConstructor = window.AudioContext || (window as AudioWindow).webkitAudioContext;
        if (!AudioContextConstructor) {
          throw new Error('AUDIO_MIXING_UNAVAILABLE');
        }

        const context = new AudioContextConstructor();
        audioContextRef.current = context;
        const destination = context.createMediaStreamDestination();
        context.createMediaStreamSource(microphoneStream).connect(destination);
        context.createMediaStreamSource(displayStream).connect(destination);
        recordingStream = destination.stream;
      } else {
        microphoneStream = await navigator.mediaDevices.getUserMedia(micConstraint as MediaStreamConstraints);
        if (!mountedRef.current || captureGenerationRef.current !== generation) {
          microphoneStream.getTracks().forEach((track) => track.stop());
          return;
        }
        sourceStreamsRef.current = [microphoneStream];
        recordingStream = microphoneStream;
      }

      if (captureGenerationRef.current !== generation || !mountedRef.current) {
        recordingStream.getTracks().forEach((track) => track.stop());
        releaseCapture();
        return;
      }

      outputStreamRef.current = recordingStream;
      const recorder = new MediaRecorder(recordingStream, { mimeType: mimeTypeRef.current });
      recorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (captureGenerationRef.current === generation && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstart = () => {
        if (captureGenerationRef.current !== generation || !mountedRef.current) return;
        segmentStartMsRef.current = Date.now();
        setPhase('recording');
        beginTimer();
      };

      recorder.onpause = () => {
        stopTimerAndCaptureSegment();
        if (captureGenerationRef.current === generation && mountedRef.current) {
          setPhase('paused');
        }
      };

      recorder.onresume = () => {
        if (captureGenerationRef.current === generation && mountedRef.current) {
          segmentStartMsRef.current = Date.now();
          setPhase('recording');
          beginTimer();
        }
      };

      recorder.onerror = () => {
        if (captureGenerationRef.current !== generation) return;
        captureGenerationRef.current += 1;
        recorder.ondataavailable = null;
        recorder.onstop = null;
        recorder.onpause = null;
        recorder.onresume = null;
        recorder.onerror = null;
        recorder.onstart = null;
        recorderRef.current = null;

        if (recorder.state !== 'inactive') {
          recorder.stop();
        }
        releaseCapture();

        if (mountedRef.current) {
          setError('Recording stopped unexpectedly. Check your browser permissions and try again.');
          setPhase('idle');
        }
      };

      recorder.onstop = () => {
        const durationSeconds = finalizeDurationSeconds();
        stopTimerAndCaptureSegment();

        const chunks = chunksRef.current;
        chunksRef.current = [];
        recorderRef.current = null;
        releaseCapture();

        if (!mountedRef.current || cancelledRef.current || captureGenerationRef.current !== generation) {
          if (mountedRef.current) {
            setElapsedSeconds(0);
            setPhase('idle');
          }
          return;
        }

        const blob = new Blob(chunks, { type: mimeTypeRef.current || 'audio/webm' });
        if (blob.size === 0) {
          setError('No sound was captured. Check the selected source and try again.');
          setPhase('idle');
          return;
        }

        const fileType = mimeTypeRef.current || 'audio/webm';
        const extension = recorderMimeToExtension(fileType);
        const file = new File([blob], `meetly-${Date.now()}.${extension}`, {
          type: mimeTypeRef.current || 'audio/webm',
        });

        onRecordingReady({
          file,
          durationSeconds,
          captureMode,
        });

        setElapsedSeconds(durationSeconds);
        setPhase('ready');
      };

      recorder.start(1000);
      if (!mountedRef.current || captureGenerationRef.current !== generation) {
        recorder.ondataavailable = null;
        recorder.onstart = null;
        recorder.onpause = null;
        recorder.onresume = null;
        recorder.onstop = null;
        recorder.onerror = null;
        if (recorder.state !== 'inactive') {
          recorder.stop();
        }
        recorderRef.current = null;
        releaseCapture();
      }
    } catch (caught) {
      if (microphoneStream && !sourceStreamsRef.current.includes(microphoneStream)) {
        sourceStreamsRef.current.push(microphoneStream);
      }

      if (displayStream && !sourceStreamsRef.current.includes(displayStream)) {
        sourceStreamsRef.current.push(displayStream);
      }
      releaseCapture();

      if (!mountedRef.current || captureGenerationRef.current !== generation) return;

      const reason = caught instanceof Error ? caught.message : '';
      if (reason === 'DISPLAY_AUDIO_MISSING') {
        setError('Share audio from the selected tab or screen, then try again.');
      } else if (reason === 'DISPLAY_CAPTURE_UNAVAILABLE' || reason === 'AUDIO_MIXING_UNAVAILABLE') {
        setError('System or tab audio capture is not supported in this browser. Microphone-only recording is still available.');
      } else if (caught instanceof DOMException && (caught.name === 'NotAllowedError' || caught.name === 'SecurityError')) {
        setError('Recording permission was denied. Allow access in your browser, then try again.');
      } else {
        setError('Meetly could not start recording. Check your selected source and browser permissions.');
      }
      setPhase('idle');
    }
  };

  const stopRecording = () => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    stopTimerAndCaptureSegment();
    setPhase('stopping');
    recorder.stop();
  };

  const pauseRecording = () => {
    if (!recorderSupportsPause) return;
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== 'recording') return;
    recorder.pause();
  };

  const resumeRecording = () => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== 'paused') return;
    recorder.resume();
  };

  const cancelRecording = () => {
    cancelledRef.current = true;
    captureGenerationRef.current += 1;
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      stopTimerAndCaptureSegment();
      recorder.stop();
    } else {
      chunksRef.current = [];
      releaseCapture();
      setElapsedSeconds(0);
      setPhase('idle');
    }
  };

  return (
    <section className="overflow-hidden rounded-[24px] border border-[var(--gt-border)] bg-[var(--gt-surface-0)] shadow-[var(--gt-elev-3)]">
      <div className="flex flex-col gap-3 border-b border-[var(--gt-divider)] bg-[var(--gt-surface-1)] px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2.5 text-sm font-semibold text-[var(--gt-text-primary)]">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--gt-primary-container)] text-[var(--gt-primary)]">
            <Mic aria-hidden="true" size={16} />
          </span>
          Capture workspace
        </div>
        <div className="flex items-center gap-2 font-mono text-xs tracking-wide text-[var(--gt-text-secondary)]" aria-live="polite">
          <span className={`h-2 w-2 rounded-full ${phase === 'recording' || phase === 'paused' ? 'animate-pulse bg-[#e66a52] motion-reduce:animate-none' : 'bg-[var(--gt-border-strong)]'}`} />
          {startHintFor(phase, phaseLabel)}
        </div>
      </div>

      <div className="grid gap-8 p-5 sm:p-7 lg:grid-cols-[minmax(0,1fr)_260px]">
        <div className="flex min-h-[340px] flex-col items-center justify-center rounded-[20px] border border-[var(--gt-border)] bg-[var(--gt-surface-2)] px-5 py-10 text-center">
          <div className={`relative flex h-28 w-28 items-center justify-center rounded-full border ${phase === 'recording' ? 'border-[#e66a52]/40 bg-[#fff1ed]' : phase === 'ready' ? 'border-[var(--gt-accent-soft)] bg-[var(--gt-primary-container)]' : 'border-[var(--gt-border-strong)] bg-[var(--gt-surface-0)]'}`}>
            {phase === 'recording' && <span className="absolute inset-3 animate-ping rounded-full bg-[#e66a52]/15 motion-reduce:animate-none" aria-hidden="true" />}
            {phase === 'ready'
              ? <Check size={36} aria-hidden="true" className="text-[var(--gt-primary)]" />
              : <span className={`relative h-11 w-11 rounded-full ${phase === 'recording' || phase === 'paused' ? 'bg-[#e66a52]' : 'bg-[var(--gt-primary)]'}`} aria-hidden="true" />}
          </div>

          <h2 className="gt-display mt-6 text-xl text-[var(--gt-text-primary)]">
            {phase === 'recording' ? 'Meeting capture in progress' : phase === 'paused' ? 'Recording is paused' : phase === 'ready' ? 'Ready for private processing' : 'Record the conversation first'}
          </h2>
          <p className="mt-2 max-w-md text-sm leading-6 text-[var(--gt-text-secondary)]">
            {phase === 'recording' || phase === 'paused' ? 'Keep this page open. Stop when the conversation is finished.' : phase === 'ready' ? 'Your capture is held only long enough to send it securely through TeamLio.' : 'Meetly will process the meeting after you stop and choose Process meeting.'}
          </p>

          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            {phase === 'recording' || phase === 'paused' ? (
              <>
                {recorderSupportsPause ? (
                  phase === 'recording' ? (
                    <button
                      type="button"
                      onClick={pauseRecording}
                      className="inline-flex min-h-11 items-center gap-2 rounded-full bg-[var(--gt-primary)] px-6 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-[var(--gt-primary-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] focus-visible:ring-offset-2"
                    >
                      <Pause size={15} aria-hidden="true" /> Pause recording
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={resumeRecording}
                      className="inline-flex min-h-11 items-center gap-2 rounded-full bg-[var(--gt-primary)] px-6 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-[var(--gt-primary-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] focus-visible:ring-offset-2"
                    >
                      <Play size={15} aria-hidden="true" /> Resume recording
                    </button>
                  )
                ) : null}
                <button
                  type="button"
                  onClick={stopRecording}
                  className="inline-flex min-h-11 items-center gap-2 rounded-full bg-[#e66a52] px-6 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-[#ce5945] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e66a52] focus-visible:ring-offset-2"
                >
                  <Square size={15} fill="currentColor" aria-hidden="true" /> Stop recording
                </button>
                <button
                  type="button"
                  onClick={cancelRecording}
                  className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[var(--gt-border-strong)] bg-white px-5 py-3 text-sm font-semibold text-[var(--gt-text-secondary)] transition hover:bg-[var(--gt-surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] focus-visible:ring-offset-2"
                >
                  <X size={16} aria-hidden="true" /> Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => void startRecording()}
                disabled={disabled || isActive}
                className="inline-flex min-h-12 items-center gap-2 rounded-full bg-[var(--gt-primary)] px-7 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-[var(--gt-primary-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Mic size={18} aria-hidden="true" /> {phase === 'ready' ? 'Record again' : phase === 'starting' ? 'Preparing…' : 'Start recording'}
              </button>
            )}
          </div>

          {error && <p role="alert" className="mt-5 max-w-lg rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>}
        </div>

        <fieldset className="self-start" disabled={isActive || disabled}>
          <legend className="gt-eyebrow mb-3">Capture source</legend>
          <div className="space-y-3">
            <label className={`block cursor-pointer rounded-2xl border p-4 transition focus-within:ring-2 focus-within:ring-[var(--gt-primary)] focus-within:ring-offset-2 ${captureMode === 'microphone' ? 'border-[var(--gt-primary)] bg-[var(--gt-primary-container)]' : 'border-[var(--gt-border)] bg-white hover:border-[var(--gt-border-strong)]'}`}>
              <span className="flex items-start gap-3">
                <input
                  type="radio"
                  name="meetly-capture-mode"
                  value="microphone"
                  checked={captureMode === 'microphone'}
                  onChange={() => setCaptureMode('microphone')}
                  className="mt-1 h-4 w-4 accent-[var(--gt-primary)]"
                />
                <span>
                  <span className="flex items-center gap-2 text-sm font-bold text-[var(--gt-text-primary)]">
                    <Mic size={15} aria-hidden="true" /> Microphone only
                  </span>
                  <span className="mt-1 block text-xs leading-5 text-[var(--gt-text-secondary)]">Default. Captures voices heard by your microphone.</span>
                </span>
              </span>
            </label>

            <label className={`block cursor-pointer rounded-2xl border p-4 transition focus-within:ring-2 focus-within:ring-[var(--gt-primary)] focus-within:ring-offset-2 ${captureMode === 'microphone-and-system' ? 'border-[var(--gt-primary)] bg-[var(--gt-primary-container)]' : 'border-[var(--gt-border)] bg-white hover:border-[var(--gt-border-strong)]'}`}>
              <span className="flex items-start gap-3">
                <input
                  type="radio"
                  name="meetly-capture-mode"
                  value="microphone-and-system"
                  checked={captureMode === 'microphone-and-system'}
                  onChange={() => setCaptureMode('microphone-and-system')}
                  className="mt-1 h-4 w-4 accent-[var(--gt-primary)]"
                />
                <span>
                  <span className="flex items-center gap-2 text-sm font-bold text-[var(--gt-text-primary)]">
                    <MonitorUp size={15} aria-hidden="true" /> Microphone + system/tab audio
                  </span>
                  <span className="mt-1 block text-xs leading-5 text-[var(--gt-text-secondary)]">Optional. Your browser will ask what to share. Turn on “Share audio” in that prompt.</span>
                </span>
              </span>
            </label>
          </div>

          <div className="mt-4 space-y-3">
            {microphones.length > 1 && (
              <label className="flex flex-col gap-2 rounded-2xl border border-[var(--gt-border)] bg-white p-4">
                <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--gt-text-secondary)]">Microphone</span>
                <select
                  aria-label="Select microphone"
                  value={selectedMicrophoneId}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSelectedMicrophoneId(value);
                    try {
                      window.localStorage.setItem(microphonePreferenceKey, value);
                    } catch {
                      // Ignore localStorage failures in restricted environments.
                    }
                  }}
                  className="w-full rounded border border-[var(--gt-border)] bg-white px-3 py-2 text-sm"
                >
                  {microphones.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label || `Microphone ${device.deviceId}`}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <div className="mt-5 flex gap-3 rounded-2xl border border-[var(--gt-border)] bg-[var(--gt-surface-1)] p-4">
            <ShieldCheck className="mt-0.5 shrink-0 text-[var(--gt-primary)]" size={18} aria-hidden="true" />
            <p className="text-xs leading-5 text-[var(--gt-text-secondary)]">{permissionHint}</p>
          </div>
        </fieldset>
      </div>
    </section>
  );
}

export default MeetlyRecorder;
